import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ShieldCheck,
  Building2,
  Users,
  KeyRound,
  ArrowRight,
  Globe,
  FileSignature,
  Server,
} from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import heroAsset from "@/assets/solutions/enterprise/hero.webp.asset.json";
import identityAsset from "@/assets/solutions/enterprise/identity.webp.asset.json";
import brandAsset from "@/assets/solutions/enterprise/brand.webp.asset.json";
import securityAsset from "@/assets/solutions/enterprise/security.webp.asset.json";

const hero = heroAsset.url;
const sso = identityAsset.url;
const brand = brandAsset.url;
const security = securityAsset.url;

const heroAlt =
  "Banana enterprise team workspace on a MacBook showing shared branded business card templates, email signatures and brand assets organized by department for a 10,000-seat rollout";
const identityAlt =
  "Enterprise admin inviting a new member with role-based page access toggles, pooled team seats and plan limits for AI usage, cards, workspaces and automations via SSO and SCIM provisioning";
const brandAlt =
  "Central Banana brand asset hub on iPad showing 107 approved logos, brochures, floorplans and marketing collateral organized in folders for regional enterprise teams to reuse";
const securityAlt =
  "Fan of custom-branded physical NFC business cards for enterprise employees with locked design tokens, corporate logos and secure encrypted tap-to-share credentials";

export const Route = createFileRoute("/solutions/enterprise")({
  head: () => ({
    meta: [
      { title: "NFC business cards & CRM for Enterprise — banana.cards" },
      {
        name: "description",
        content:
          "SSO, SCIM, SOC 2, per-seat billing and a dedicated success team. Roll out branded cards and AI CRM to 10,000+ employees in days, not quarters.",
      },
      { property: "og:title", content: "NFC business cards & CRM for Enterprise — banana.cards" },
      {
        property: "og:description",
        content: "SSO, SCIM, SOC 2, dedicated success, 99.9% SLA.",
      },
      { property: "og:image", content: hero },
      { property: "og:url", content: "https://banana.cards/solutions/enterprise" },
    ],
    links: [{ rel: "canonical", href: "https://banana.cards/solutions/enterprise" }],
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="For Enterprise"
      title="Built for"
      italic="scale."
      description="SAML SSO, SCIM provisioning, audit logs, custom DPAs and a 99.9% SLA — wrapped around the same Banana platform your reps already love."
      heroImage={hero}
      heroImageAlt={heroAlt}
      primaryCta={{ label: "Talk to sales", to: "/contact" }}
      secondaryCta={{ label: "Download security pack", to: "/security" }}
      metrics={[
        { v: "10k+", l: "Seats provisioned in 24h" },
        { v: "99.9%", l: "Uptime SLA" },
        { v: "SOC 2", l: "Type II certified" },
      ]}
      bullets={[
        {
          icon: KeyRound,
          title: "SSO, SCIM & RBAC",
          desc: "Okta, Azure AD, Google Workspace and any SAML 2.0 IdP. Just-in-time provisioning, group-based roles and offboarding in one click.",
        },
        {
          icon: Building2,
          title: "Branded at every level",
          desc: "Org-wide card templates, regional sub-brands, custom domains, white-labeled profiles and lockable design tokens for your design system team.",
        },
        {
          icon: ShieldCheck,
          title: "Security & compliance built-in",
          desc: "SOC 2 Type II, ISO 27001, GDPR, CCPA. Custom DPA, data residency in EU/US/APAC, audit logs streamed to your SIEM.",
        },
      ]}
      deepDives={[
        {
          eyebrow: "Identity",
          title: "Provision 10,000 cards",
          italic: "in a single afternoon.",
          description:
            "Push your directory through SCIM. Banana creates branded digital profiles, ships physical NFC cards to each employee's home address, and revokes access the moment HR offboards them.",
          points: [
            "SAML 2.0 SSO (Okta, Azure AD, Google, Ping, OneLogin)",
            "SCIM 2.0 just-in-time provisioning",
            "Group-based roles & permission scopes",
            "Bulk card shipping to home addresses worldwide",
          ],
          image: sso,
          imageAlt: identityAlt,
        },
        {
          eyebrow: "Brand",
          title: "Your brand on every",
          italic: "tap, scan and signature.",
          description:
            "Design once at HQ, lock the system, and let regional teams use it. Templates, fonts, colors, layouts and approved imagery — controlled, versioned and rolled out instantly.",
          points: [
            "Org & sub-brand template libraries",
            "Custom domain & white-label profiles",
            "Approved imagery & font lockdown",
            "Email signature deployment for every seat",
          ],
          image: brand,
          imageAlt: brandAlt,
          reverse: true,
        },
        {
          eyebrow: "Security",
          title: "What your CISO",
          italic: "wants to read.",
          description:
            "SOC 2 Type II, ISO 27001, penetration tests, vulnerability disclosure program, and end-to-end encryption on notes. Choose data residency in EU, US or APAC. Custom DPA and BAA available.",
          points: [
            "SOC 2 Type II & ISO 27001",
            "EU / US / APAC data residency",
            "Audit log streaming (Splunk, Datadog, S3)",
            "Custom DPA, BAA & MSA available",
          ],
          image: security,
          imageAlt: securityAlt,
        },
      ]}
      howItWorks={[
        { title: "Kickoff & scoping", desc: "Dedicated solutions engineer maps your IdP, brand system, regions and rollout phases." },
        { title: "Pilot in 2 weeks", desc: "100-seat pilot with full SSO, branded cards shipped and CRM integrations live." },
        { title: "Roll out globally", desc: "SCIM provisioning + bulk card shipping covers thousands of seats across regions in days." },
        { title: "Ongoing success", desc: "Named CSM, quarterly business reviews, 24/7 enterprise support and roadmap input." },
      ]}
      capabilities={[
        "SAML 2.0 SSO & SCIM provisioning",
        "Role-based access control",
        "SOC 2 Type II & ISO 27001",
        "Custom DPA, BAA & MSA",
        "EU / US / APAC data residency",
        "Audit log streaming to your SIEM",
        "Salesforce, HubSpot & Workday sync",
        "Dedicated CSM & 24/7 support",
        "99.9% uptime SLA",
        "Custom domain & white-label",
      ]}
      faq={[
        {
          q: "How does SSO and SCIM work?",
          a: "We support any SAML 2.0 identity provider — Okta, Azure AD, Google Workspace, Ping, OneLogin, Duo and more. SCIM 2.0 handles just-in-time provisioning: new hires get a profile and a card shipped the day HR adds them, and offboarding revokes access instantly across web, mobile and the physical NFC chip.",
        },
        {
          q: "Where is our data stored?",
          a: "You choose. Banana operates regional data planes in the EU (Frankfurt), US (Virginia) and APAC (Singapore). All data — profiles, notes, recordings — stays in your chosen region. Cross-region replication is opt-in and disabled by default for regulated industries.",
        },
        {
          q: "What compliance certifications do you hold?",
          a: "SOC 2 Type II, ISO 27001, GDPR and CCPA compliant. HIPAA-ready with a signed BAA available on Enterprise. Annual third-party penetration tests, public vulnerability disclosure program and a security trust center with current reports.",
        },
        {
          q: "Can we white-label the product?",
          a: "Yes. Custom domain, removed Banana branding, your logo and color tokens on every surface — profile pages, emails, wallet passes, signature blocks. Enterprise plan includes co-branded mobile apps for the largest deployments.",
        },
        {
          q: "What does pricing look like at 10,000 seats?",
          a: "Per-seat with volume tiers. Includes unlimited cards (you only pay shipping & material on physical replacements), unlimited AI usage within fair-use, all integrations, and dedicated success. Typical 10k-seat deployments come in well below the cost of a single full-time CRM administrator.",
        },
        {
          q: "How fast can we go live?",
          a: "Pilot of 100 seats in 2 weeks. Full rollout to 10,000+ seats typically takes 4–6 weeks end-to-end, including procurement, SSO config, brand template approval and bulk card shipping to home addresses worldwide.",
        },
      ]}
      outro={{
        title: "Ready when",
        italic: "you are.",
        description: "Get a custom demo, security pack and pricing model built around your seat count and regions.",
      }}
      afterFaq={<EntUseCases />}
    />
  );
}

function EntUseCases() {
  const items = [
    { i: Globe, t: "Global rollouts", d: "Multi-region data planes, bulk card shipping to 80+ countries, language-aware profiles.", to: "/platform" },
    { i: Users, t: "Sales orgs at scale", d: "Per-team templates, manager dashboards, Salesforce & HubSpot two-way sync.", to: "/solutions/sales" },
    { i: FileSignature, t: "Regulated industries", d: "HIPAA BAA, audit logs, signed contracts, data residency controls.", to: "/security" },
    { i: Server, t: "IT & security teams", d: "SSO, SCIM, SIEM streaming, single trust center, named security contact.", to: "/security" },
  ];
  return (
    <section className="bg-background">
      <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Enterprise use cases</p>
        <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
          Built for the way <em className="italic">large orgs actually buy.</em>
        </h2>
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {items.map((i) => {
            const Icon = i.i;
            return (
              <Link key={i.t} to={i.to} className="group rounded-2xl bg-muted p-6 transition hover:bg-muted/70">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-soft">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-bold">{i.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{i.d}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold underline-offset-4 group-hover:underline">
                  Explore <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
